// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { formatBalance, CoinFormat, formatAmount, formatDate } from '@mysten/core';
import { Heading, Text, LoadingIndicator } from '@mysten/ui';
import { type ColumnDef } from '@tanstack/react-table';
import { ParentSize } from '@visx/responsive';
import BigNumber from 'bignumber.js';
import clsx from 'clsx';
import { type ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
	COINS_API_URL,
	COIN_TX_PAGE_SIZE,
	HOLDERS_PAGE_SIZE,
	useCoinDetail,
	useCoinHolders,
	useCoinPriceHistory,
	useCoinTransactions,
	type CoinDetail as CoinDetailData,
	type CoinHolder,
	type CoinPriceChange,
	type CoinPricePoint,
	type CoinTradingStats,
	type CoinTransaction,
	type PriceHistoryRange,
} from '~/hooks/useCoins';
import { AreaGraph } from '~/components/AreaGraph';
import { ErrorBoundary } from '~/components/error-boundary/ErrorBoundary';
import { PageLayout } from '~/components/Layout/PageLayout';
import { CoinAvatar } from './CoinAvatar';
import { formatCreateTime, formatPercentChange, formatUsd } from './format';
import { SenderLabel } from '~/components/SenderLabel';
import { Banner } from '~/ui/Banner';
import { Card } from '~/ui/Card';
import { CopyToClipboard } from '~/ui/CopyToClipboard';
import { DescriptionItem, DescriptionList } from '~/ui/DescriptionList';
import { ReactComponent as InfoSvg } from '~/ui/icons/info_10x10.svg';
import { AddressLink, ObjectLink, TransactionLink } from '~/ui/InternalLink';
import { Link } from '~/ui/Link';
import { Pagination } from '~/ui/Pagination';
import { PlaceholderTable } from '~/ui/PlaceholderTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/ui/Tabs';
import { TableCard } from '~/ui/TableCard';
import { Tooltip } from '~/ui/Tooltip';

// ---------------------------------------------------------------------------
// Price chart
// ---------------------------------------------------------------------------

function PriceTooltip({ data }: { data: CoinPricePoint }) {
	return (
		<div className="flex flex-col gap-0.5">
			<Text variant="subtitleSmallExtra/medium" color="steel-darker">
				{formatDate(new Date(data.t), ['day', 'month', 'year'])}
			</Text>
			<Heading variant="heading6/semibold" color="steel-darker">
				{formatUsd(data.price)}
			</Heading>
		</div>
	);
}

const RANGES: PriceHistoryRange[] = ['30d', 'max'];

function PriceChart({ coin }: { coin: CoinDetailData }) {
	const [range, setRange] = useState<PriceHistoryRange>('max');
	const { data, isPending } = useCoinPriceHistory(coin.coinType, range);
	const points = data?.points ?? [];

	return (
		<Card bg="white" border="gray45" rounded="2xl" spacing="lgGraph" height="full">
			<div className="flex h-full flex-col gap-4">
				<div className="flex items-start justify-between">
					<div className="flex flex-col gap-1">
						<Text variant="bodySmall/semibold" color="steel-dark">
							Coin Price
						</Text>
						<Heading variant="heading3/semibold" color="steel-darker">
							{coin.priceUsd != null ? formatUsd(coin.priceUsd) : '--'}
						</Heading>
					</div>
					<div className="flex rounded-lg border border-gray-45 p-0.5">
						{RANGES.map((r) => (
							<button
								key={r}
								type="button"
								onClick={() => setRange(r)}
								className={clsx(
									'rounded-md px-3 py-1 text-bodySmall font-medium capitalize',
									range === r ? 'bg-safe/10 text-hero-dark' : 'text-steel-dark hover:text-steel-darker',
								)}
							>
								{r === 'max' ? 'Max' : r}
							</button>
						))}
					</div>
				</div>

				<div className="relative flex min-h-[280px] flex-1 items-center justify-center">
					{isPending ? (
						<div className="flex flex-col items-center gap-1">
							<LoadingIndicator />
							<Text color="steel" variant="body/medium">
								loading price data
							</Text>
						</div>
					) : points.length > 1 ? (
						<div className="absolute inset-0">
							<ErrorBoundary>
								<ParentSize>
									{({ height, width }) => (
										<AreaGraph
											data={points}
											height={height}
											width={width}
											getX={({ t }) => t}
											getY={({ price }) => price}
											color="blue"
											formatX={(t) => formatDate(new Date(t), ['month', 'year'])}
											formatY={formatUsd}
											tooltipContent={PriceTooltip}
										/>
									)}
								</ParentSize>
							</ErrorBoundary>
						</div>
					) : (
						<Text color="steel" variant="body/medium">
							No price history available
						</Text>
					)}
				</div>
			</div>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Right rail: stat tiles, change tabs, trading stats
// ---------------------------------------------------------------------------

function formatSupplyTile(base: string | null, decimals: number | null) {
	if (base == null || decimals == null) return '--';
	return formatBalance(base, decimals, CoinFormat.ROUNDED);
}

function StatGrid({ coin }: { coin: CoinDetailData }) {
	const tiles: { label: string; value: string; tooltip?: string }[] = [
		{ label: 'Price', value: coin.priceUsd != null ? formatUsd(coin.priceUsd) : '--' },
		{ label: 'Liquidity', value: coin.liquidityUsd != null ? formatUsd(coin.liquidityUsd) : '--' },
		{ label: 'FDV', value: coin.fdvUsd != null ? formatUsd(coin.fdvUsd) : '--', tooltip: 'Fully diluted valuation = price × total supply' },
		{ label: 'MKT Cap', value: coin.marketCapUsd != null ? formatUsd(coin.marketCapUsd) : '--', tooltip: 'Price × circulating supply' },
		{ label: 'Total Supply', value: formatSupplyTile(coin.totalSupply, coin.decimals) },
		{ label: 'Cir Supply', value: formatSupplyTile(coin.circulatingSupply, coin.decimals) },
	];
	return (
		<Card bg="white" border="gray45" rounded="2xl" spacing="none">
			<div className="grid grid-cols-3">
				{tiles.map((tile, i) => (
					<div
						key={tile.label}
						className={clsx(
							'flex flex-col gap-1 p-4',
							i % 3 !== 2 && 'border-r border-gray-45',
							i < 3 && 'border-b border-gray-45',
						)}
					>
						<div className="flex items-center gap-1">
							<Text variant="captionSmall/medium" color="steel-dark">
								{tile.label}
							</Text>
							{tile.tooltip && (
								<Tooltip tip={tile.tooltip}>
									<InfoSvg />
								</Tooltip>
							)}
						</div>
						<Text variant="body/semibold" color="steel-darker">
							{tile.value}
						</Text>
					</div>
				))}
			</div>
		</Card>
	);
}

function ChangeValue({ value }: { value: number | null }) {
	const change = formatPercentChange(value);
	if (!change) {
		return (
			<Text variant="bodySmall/medium" color="steel-dark">
				-
			</Text>
		);
	}
	return (
		<span
			className={clsx(
				'text-bodySmall font-medium',
				change.positive ? 'text-success-dark' : 'text-issue-dark',
			)}
		>
			{change.text}
		</span>
	);
}

function ChangeTabs({ priceChange }: { priceChange: CoinPriceChange | null }) {
	const items: { label: string; value: number | null }[] = [
		{ label: '30M', value: priceChange?.m30 ?? null },
		{ label: '1H', value: priceChange?.h1 ?? null },
		{ label: '4H', value: priceChange?.h4 ?? null },
		{ label: '24H', value: priceChange?.h24 ?? null },
	];
	return (
		<div className="flex rounded-xl border border-gray-45 p-1">
			{items.map((item, i) => (
				<div
					key={item.label}
					className={clsx(
						'flex flex-1 flex-col items-center gap-1 rounded-lg py-2',
						i === 0 && 'bg-white shadow-button',
					)}
				>
					<Text variant="captionSmall/medium" color="steel-dark">
						{item.label}
					</Text>
					<ChangeValue value={item.value} />
				</div>
			))}
		</div>
	);
}

function ProportionBar({ left, right }: { left: number | null; right: number | null }) {
	const l = left ?? 0;
	const r = right ?? 0;
	const total = l + r;
	const leftPct = total > 0 ? (l / total) * 100 : 50;
	return (
		<div className="flex h-1.5 w-full gap-1">
			<div className="h-full rounded-full bg-success" style={{ width: `${leftPct}%` }} />
			<div className="h-full flex-1 rounded-full bg-issue" />
		</div>
	);
}

function StatCell({ label, value, align = 'left' }: { label: string; value: string; align?: 'left' | 'right' }) {
	return (
		<div className={clsx('flex flex-col gap-0.5', align === 'right' && 'items-end text-right')}>
			<Text variant="captionSmall/medium" color="steel-dark">
				{label}
			</Text>
			<Text variant="bodySmall/semibold" color="steel-darker">
				{value}
			</Text>
		</div>
	);
}

function count(value: number | null) {
	return value != null ? value.toLocaleString('en-US') : '--';
}

function usd(value: number | null) {
	return value != null ? formatUsd(value) : '--';
}

function TradingStats({ trading }: { trading: CoinTradingStats | null }) {
	const rows = [
		{
			left: { label: 'TXNS', value: count(trading?.txns ?? null) },
			mid: { label: 'Buys', value: count(trading?.buys ?? null) },
			right: { label: 'Sells', value: count(trading?.sells ?? null) },
			barLeft: trading?.buys ?? null,
			barRight: trading?.sells ?? null,
		},
		{
			left: { label: 'Volume', value: usd(trading?.volumeUsd ?? null) },
			mid: { label: 'Buy Vol', value: usd(trading?.buyVolumeUsd ?? null) },
			right: { label: 'Sell Vol', value: usd(trading?.sellVolumeUsd ?? null) },
			barLeft: trading?.buyVolumeUsd ?? null,
			barRight: trading?.sellVolumeUsd ?? null,
		},
		{
			left: { label: 'Makers', value: count(trading?.makers ?? null) },
			mid: { label: 'Buyers', value: count(trading?.buyers ?? null) },
			right: { label: 'Sellers', value: count(trading?.sellers ?? null) },
			barLeft: trading?.buyers ?? null,
			barRight: trading?.sellers ?? null,
		},
	];
	return (
		<div className="flex flex-col gap-5">
			{rows.map((row) => (
				<div key={row.left.label} className="flex flex-col gap-2">
					<div className="grid grid-cols-3 items-start">
						<StatCell label={row.left.label} value={row.left.value} />
						<StatCell label={row.mid.label} value={row.mid.value} />
						<StatCell label={row.right.label} value={row.right.value} align="right" />
					</div>
					<ProportionBar left={row.barLeft} right={row.barRight} />
				</div>
			))}
		</div>
	);
}

function OtherInfo({ coin, coinType }: { coin: CoinDetailData; coinType: string }) {
	const packageId = coinType.split('::')[0];
	const created =
		coin.createdAtMs != null
			? `${formatDate(new Date(coin.createdAtMs), ['day', 'month', 'year', 'hour', 'minute'])}`
			: null;
	return (
		<Card bg="white" border="gray45" rounded="2xl" spacing="lg">
			<div className="flex flex-col gap-4">
				<Heading variant="heading6/semibold" color="steel-darker">
					Other Info
				</Heading>
				<DescriptionList>
					<DescriptionItem title="Package ID">
						<ObjectLink objectId={packageId} />
					</DescriptionItem>
					<DescriptionItem title="Creator">
						{coin.creator ? (
							<AddressLink address={coin.creator} />
						) : (
							<Text variant="pBody/medium" color="steel-darker">
								--
							</Text>
						)}
					</DescriptionItem>
					<DescriptionItem title="Created">
						{created ? (
							<div className="flex flex-col items-end gap-0.5 sm:items-start">
								<Text variant="pBody/medium" color="steel-darker">
									{created}
								</Text>
								<span className="text-captionSmall font-medium text-steel-dark">
									{formatCreateTime(coin.createdAtMs)} ago
								</span>
							</div>
						) : (
							<Text variant="pBody/medium" color="steel-darker">
								--
							</Text>
						)}
					</DescriptionItem>
					<DescriptionItem title="Decimals">
						<Text variant="pBody/medium" color="steel-darker">
							{coin.decimals ?? '--'}
						</Text>
					</DescriptionItem>
					<DescriptionItem title="Symbol">
						<Text variant="pBody/medium" color="steel-darker">
							{coin.symbol ?? '--'}
						</Text>
					</DescriptionItem>
				</DescriptionList>
			</div>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Tabs: Holders + Transaction Blocks
// ---------------------------------------------------------------------------

function ShareBar({ percent }: { percent: BigNumber }) {
	const width = Math.min(percent.toNumber(), 100);
	return (
		<div className="flex items-center gap-2">
			<div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-45">
				<div className="h-full rounded-full bg-safe" style={{ width: `${width}%` }} />
			</div>
			<Text variant="bodySmall/medium" color="steel-darker">
				{percent.lt(0.0001) && percent.gt(0) ? '<0.0001' : percent.toFormat(4)}%
			</Text>
		</div>
	);
}

function holderColumns(coin: CoinDetailData, offset: number): ColumnDef<CoinHolder>[] {
	const supply = coin.totalSupply ? new BigNumber(coin.totalSupply) : null;
	return [
		{
			header: '#',
			accessorKey: 'owner',
			id: 'rank',
			cell: (props) => (
				<Text variant="bodySmall/medium" color="steel-dark">
					{offset + props.row.index + 1}
				</Text>
			),
		},
		{
			header: 'Holder',
			accessorKey: 'owner',
			id: 'holder',
			cell: (props) => <AddressLink address={props.row.original.owner} />,
		},
		{
			header: 'Amount',
			accessorKey: 'balance',
			cell: (props) => {
				const { balance } = props.row.original;
				const formatted =
					coin.decimals != null ? formatBalance(balance, coin.decimals, CoinFormat.FULL) : balance;
				return (
					<Text variant="bodySmall/medium" color="steel-darker">
						{formatted} {coin.symbol ?? ''}
					</Text>
				);
			},
		},
		{
			header: 'Percentage',
			accessorKey: 'balance',
			id: 'percent',
			cell: (props) => {
				if (!supply || supply.isZero()) return <Text variant="bodySmall/medium">--</Text>;
				const pct = new BigNumber(props.row.original.balance).div(supply).times(100);
				return <ShareBar percent={pct} />;
			},
		},
		{
			header: 'Objects',
			accessorKey: 'objects',
			cell: (props) => (
				<Text variant="bodySmall/medium" color="steel-darker">
					{props.row.original.objects.toLocaleString('en-US')}
				</Text>
			),
		},
	];
}

function HoldersTab({ coin }: { coin: CoinDetailData }) {
	const [page, setPage] = useState(0);
	const { data, isPending, isPlaceholderData } = useCoinHolders(coin.coinType, page);
	const total = data?.total ?? coin.holders;
	const pageCount = Math.max(Math.ceil(total / HOLDERS_PAGE_SIZE), 1);

	return (
		<div className="flex flex-col gap-4">
			{isPending ? (
				<PlaceholderTable
					rowCount={10}
					rowHeight="16px"
					colHeadings={['#', 'Holder', 'Amount', 'Percentage', 'Objects']}
					colWidths={['8%', '32%', '30%', '20%', '10%']}
				/>
			) : (
				<TableCard
					refetching={isPlaceholderData}
					data={data?.holders ?? []}
					columns={holderColumns(coin, page * HOLDERS_PAGE_SIZE)}
					sortTable
				/>
			)}
			<div className="flex items-center justify-between">
				<Text variant="bodySmall/medium" color="steel-dark">
					{total.toLocaleString('en-US')} holders · page {page + 1} of{' '}
					{pageCount.toLocaleString('en-US')}
				</Text>
				<Pagination
					hasPrev={page !== 0}
					hasNext={page + 1 < pageCount}
					onFirst={() => setPage(0)}
					onPrev={() => setPage((p) => Math.max(p - 1, 0))}
					onNext={() => setPage((p) => (p + 1 < pageCount ? p + 1 : p))}
				/>
			</div>
		</div>
	);
}

function formatGas(safelet: string | null): { safe: string; safelet: string } | null {
	if (safelet == null) return null;
	return {
		safe: formatBalance(safelet, 9, CoinFormat.FULL),
		safelet: new BigNumber(safelet).toFormat(0),
	};
}

const txColumns: ColumnDef<CoinTransaction>[] = [
	{
		id: 'type',
		header: 'Type / Func',
		accessorKey: 'kind',
		cell: (props) => {
			const tx = props.row.original;
			return (
				<div className="flex flex-col gap-0.5">
					<Text variant="bodySmall/medium" color="steel-darker">
						{tx.kind}
					</Text>
					{tx.function && (
						<div className="flex items-center gap-1.5">
							<span className="text-captionSmall font-medium text-success-dark">{tx.function}</span>
							{tx.functionCount != null && tx.functionCount > 0 && (
								<span className="rounded bg-safe/10 px-1.5 text-captionSmall font-medium text-hero-dark">
									{tx.functionCount}
								</span>
							)}
						</div>
					)}
				</div>
			);
		},
	},
	{
		id: 'digest',
		header: 'Digest',
		accessorKey: 'digest',
		cell: (props) => (
			<div className="flex items-center gap-1">
				<TransactionLink digest={props.row.original.digest} />
				<CopyToClipboard size="sm" color="steel" copyText={props.row.original.digest} />
			</div>
		),
	},
	{
		id: 'age',
		header: () => (
			<div className="flex items-center gap-1">
				<span>Age</span>
				<span className="text-steel-darker">↓</span>
			</div>
		),
		accessorKey: 'timestampMs',
		cell: (props) => (
			<Text variant="bodySmall/medium" color="steel-dark">
				{formatCreateTime(props.row.original.timestampMs)}
			</Text>
		),
	},
	{
		id: 'sender',
		header: 'Sender',
		accessorKey: 'sender',
		cell: (props) => <SenderLabel address={props.row.original.sender} />,
	},
	{
		id: 'gas',
		header: () => <div className="text-right">Gas</div>,
		accessorKey: 'gasUsedSafelet',
		cell: (props) => {
			const gas = formatGas(props.row.original.gasUsedSafelet);
			return (
				<div className="flex flex-col items-end gap-0.5 text-right">
					<Text variant="bodySmall/medium" color="steel-darker">
						{gas ? `${gas.safe} SAFE` : '--'}
					</Text>
					{gas && (
						<Text variant="captionSmall/medium" color="steel-dark">
							{gas.safelet} SAFELET
						</Text>
					)}
				</div>
			);
		},
	},
];

const TX_PAGE_SIZE_OPTIONS = [10, 25, 50];

function TransactionsTab({ coin }: { coin: CoinDetailData }) {
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(COIN_TX_PAGE_SIZE);
	const { data, isPending, isPlaceholderData } = useCoinTransactions(coin.coinType, page, pageSize);
	const total = data?.total ?? 0;
	const pageCount = Math.max(Math.ceil(total / pageSize), 1);
	const transactions = data?.transactions ?? [];

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<Pagination
					hasPrev={page !== 0}
					hasNext={page + 1 < pageCount}
					onFirst={() => setPage(0)}
					onPrev={() => setPage((p) => Math.max(p - 1, 0))}
					onNext={() => setPage((p) => (p + 1 < pageCount ? p + 1 : p))}
				/>
				<div className="flex items-center gap-2">
					<Text variant="bodySmall/medium" color="steel-dark">
						Show
					</Text>
					<select
						className="form-select rounded-md border border-gray-45 px-3 py-1.5 pr-8 text-bodySmall font-medium leading-[1.2] text-steel-dark shadow-button"
						value={pageSize}
						onChange={(e) => {
							setPageSize(Number(e.target.value));
							setPage(0);
						}}
					>
						{TX_PAGE_SIZE_OPTIONS.map((size) => (
							<option key={size} value={size}>
								{size}
							</option>
						))}
					</select>
				</div>
			</div>

			{isPending ? (
				<PlaceholderTable
					rowCount={pageSize}
					rowHeight="32px"
					colHeadings={['Type / Func', 'Digest', 'Age', 'Sender', 'Gas']}
					colWidths={['26%', '22%', '10%', '22%', '20%']}
				/>
			) : transactions.length === 0 ? (
				<EmptyState message="No transaction blocks found for this coin yet." />
			) : (
				<TableCard refetching={isPlaceholderData} data={transactions} columns={txColumns} sortTable />
			)}
		</div>
	);
}

function EmptyState({ message }: { message: string }) {
	return (
		<div className="flex items-center justify-center rounded-xl border border-dashed border-gray-45 py-12">
			<Text variant="body/medium" color="steel">
				{message}
			</Text>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function CoinHeader({ coin, coinType }: { coin: CoinDetailData; coinType: string }) {
	const name = coin.name || coin.symbol || 'Unknown Coin';
	return (
		<div className="flex flex-col gap-3">
			<Link to="/coins">
				<Text variant="bodySmall/medium" color="steel-dark">
					Coins
				</Text>
			</Link>
			<div className="flex items-center gap-4">
				<CoinAvatar coin={coin} name={name} size="xl" />
				<div className="flex flex-col gap-2">
					<div className="flex flex-wrap items-center gap-3">
						<Heading as="h1" variant="heading2/semibold" color="gray-90">
							Coin: {name}
						</Heading>
						<a
							href={`${COINS_API_URL}/coins/${encodeURIComponent(coinType)}`}
							target="_blank"
							rel="noreferrer"
							className="font-mono text-bodySmall font-medium text-steel-dark hover:text-hero-dark"
						>
							{'{API}'}
						</a>
					</div>
					<div className="flex flex-wrap items-center gap-x-4 gap-y-2">
						{coin.symbol && (
							<div className="rounded-full bg-gray-45 px-2 py-0.5">
								<Text variant="captionSmall/semibold" color="steel-darker">
									{coin.symbol}
								</Text>
							</div>
						)}
						{coin.isNative && (
							<div className="rounded-full bg-safe/10 px-2 py-0.5">
								<Text variant="captionSmall/semibold" color="hero-dark">
									Native
								</Text>
							</div>
						)}
						<div className="flex items-center gap-1">
							<Text variant="bodySmall/medium" color="steel-dark">
								Type:
							</Text>
							<Text variant="bodySmall/medium" color="steel-darker" mono>
								{coinType}
							</Text>
							<CopyToClipboard size="sm" color="steel" copyText={coinType} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function TabTrigger({ value, children, count: tabCount }: { value: string; children: ReactNode; count?: ReactNode }) {
	return (
		<TabsTrigger value={value}>
			<span className="flex items-center gap-1.5">
				{children}
				{tabCount != null && (
					<span className="rounded bg-gray-40 px-1.5 py-0.5 text-captionSmall font-medium text-steel-dark">
						{tabCount}
					</span>
				)}
			</span>
		</TabsTrigger>
	);
}

export function CoinDetail() {
	const { type } = useParams();
	const coinType = type ? decodeURIComponent(type) : undefined;
	const { data, isPending, isError } = useCoinDetail(coinType);

	const coin = data?.coin;

	if (!isPending && (isError || !coin || !coinType)) {
		return (
			<PageLayout
				content={
					<Banner variant="error" fullWidth>
						Failed to load coin {coinType}.
					</Banner>
				}
			/>
		);
	}

	const content =
		!coin || !coinType ? null : (
			<div className="flex flex-col gap-8">
				<CoinHeader coin={coin} coinType={coinType} />

				<div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
					{/* Left: chart + tabs */}
					<div className="flex flex-col gap-6 lg:col-span-2">
						<PriceChart coin={coin} />

						<Tabs size="md" defaultValue="holders">
							<TabsList>
								<TabTrigger value="traders">Top Traders</TabTrigger>
								<TabTrigger value="holders" count={formatAmount(coin.holders)}>
									Holders
								</TabTrigger>
								<TabTrigger value="pools">Pools</TabTrigger>
								<TabTrigger value="transactions">Transaction Blocks</TabTrigger>
							</TabsList>
							<TabsContent value="traders">
								<EmptyState message="Top traders are coming soon." />
							</TabsContent>
							<TabsContent value="holders">
								<HoldersTab coin={coin} />
							</TabsContent>
							<TabsContent value="pools">
								<EmptyState message="Pools are coming soon." />
							</TabsContent>
							<TabsContent value="transactions">
								<TransactionsTab coin={coin} />
							</TabsContent>
						</Tabs>
					</div>

					{/* Right: stats */}
					<div className="flex flex-col gap-6">
						<StatGrid coin={coin} />
						<ChangeTabs priceChange={coin.priceChange} />
						<Card bg="white" border="gray45" rounded="2xl" spacing="lg">
							<TradingStats trading={coin.trading} />
						</Card>
						<OtherInfo coin={coin} coinType={coinType} />
					</div>
				</div>
			</div>
		);

	return <PageLayout loading={isPending} content={content} />;
}
